/**
 * 🆕 [SESSION MAX SUITE — P2 refactor BT] Context API pour cascader le state du BT
 *
 * Foundation passive : expose `form`, `setForm` et les 7 sources de vérité du BT
 * via `useContext` aux futures sous-sections extraites de `EditerBonTravailDialog.jsx`.
 *
 * **Comportement actuel** : strictement passif. Tant qu'aucune section n'est extraite
 * et ne consomme ce context, le rendu reste identique. Le wrapping du Provider
 * autour du contenu du dialog ne change AUCUN comportement.
 *
 * **Usage future** (extractions à faire en session dédiée avec validation utilisatrice) :
 * ```jsx
 * import { useBTForm } from './BTFormContext';
 *
 * function EquipeAssigneeSection() {
 *   const { form, setForm } = useBTForm();
 *   // ... section utilise form.employes_assignes, form.chef_equipe_id, etc.
 * }
 * ```
 *
 * **Les 7 sources de vérité** exposées (cohésion non-négociable Q7) :
 * - `employes_assignes` : liste des employés sur le BT
 * - `chef_equipe_id` : chef officiel
 * - `chefs_par_jour` : overrides par date (chef-du-jour, Phase 34)
 * - `chef_dates_assignees` : dates où le chef officiel est présent
 * - `dates_chantier` : période chantier
 * - `equipements_*` : flags nacelle, échafauds, etc.
 * - `presence_par_jour` : matrice présence (qui travaille quel jour)
 *
 * + `bt_jumeaux` (BT liés par jumelage) accessible aussi.
 */
import { createContext, useContext, useMemo } from 'react';

const BTFormContext = createContext(null);


export function BTFormProvider({ form, setForm, employees, bonId, extra, children }) {
  // useMemo pour éviter re-renders inutiles des consommateurs si form === form
  // 🧱 Refactor BT Phase 3 — Étape 2 : `extra` = état + handlers du dialogue parent, exposés aux sections
  //   (objet neuf à chaque rendu → recalcul à chaque rendu du parent, comme les sections inline d'origine).
  const value = useMemo(() => ({
    // State principal
    form,
    setForm,
    // Helpers de lecture des 7 sources de vérité (raccourcis)
    employes_assignes: form?.employes_assignes || [],
    chef_equipe_id: form?.chef_equipe_id || null,
    chefs_par_jour: form?.chefs_par_jour || {},
    chef_dates_assignees: form?.chef_dates_assignees || null,
    dates_chantier: form?.dates_chantier || [],
    presence_par_jour: form?.presence_par_jour || {},
    bt_jumeaux: form?.bt_jumeaux || [],
    // Catalogues partagés
    employees: employees || [],
    bonId: bonId || null,
    ...(extra || {}),
  }), [form, setForm, employees, bonId, extra]);

  return <BTFormContext.Provider value={value}>{children}</BTFormContext.Provider>;
}


/**
 * Hook pour consommer le context dans les sous-sections.
 * @throws si appelé hors d'un `BTFormProvider`.
 */
export function useBTForm() {
  const ctx = useContext(BTFormContext);
  if (ctx === null) {
    throw new Error('useBTForm doit être utilisé à l\'intérieur d\'un <BTFormProvider>');
  }
  return ctx;
}
